/**
 * Pure calendar-grid math for the assistant. Everything here works in the
 * browser's LOCAL time zone on purpose: the API stores UTC instants, and the
 * owner reads the calendar where they are sitting.
 *
 * No date library exists in this app and the bundle is already over the size
 * warning, so this stays hand-rolled and unit-tested.
 */

export interface GridEvent {
  id: string;
  startsAt: string; // ISO instant
  endsAt: string; // ISO instant
  isAllDay: boolean;
  startDate: string | null; // YYYY-MM-DD (all-day only)
  endDate: string | null; // exclusive
}

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Local civil date key of an instant, YYYY-MM-DD. */
export function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Midnight local of a YYYY-MM-DD key. */
export function fromDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

export function addDaysLocal(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

export function addDaysKey(key: string, n: number): string {
  return localDateKey(addDaysLocal(fromDateKey(key), n));
}

export function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Sunday-start week containing `d`. */
export function startOfWeek(d: Date, weekStartsOn: 0 | 1 = 0): Date {
  const day = startOfLocalDay(d);
  const diff = (day.getDay() - weekStartsOn + 7) % 7;
  return addDaysLocal(day, -diff);
}

export interface DateRange {
  /** Inclusive local midnight. */
  from: Date;
  /** Exclusive local midnight. */
  to: Date;
}

export function weekRange(anchor: Date, weekStartsOn: 0 | 1 = 0): DateRange {
  const from = startOfWeek(anchor, weekStartsOn);
  return { from, to: addDaysLocal(from, 7) };
}

/** Always six rows so the grid never jumps height between months. */
export function monthGridRange(year: number, month0: number, weekStartsOn: 0 | 1 = 0): DateRange {
  const from = startOfWeek(new Date(year, month0, 1), weekStartsOn);
  return { from, to: addDaysLocal(from, 42) };
}

export function agendaRange(anchor: Date, days = 14): DateRange {
  const from = startOfLocalDay(anchor);
  return { from, to: addDaysLocal(from, days) };
}

export function daysInRange(r: DateRange): Date[] {
  const out: Date[] = [];
  for (let d = r.from; d < r.to; d = addDaysLocal(d, 1)) out.push(d);
  return out;
}

/**
 * The local date keys an event touches, for placing it on the grid.
 * - All-day: the civil dates, end exclusive.
 * - Timed: every local day from start to end; an end exactly at midnight
 *   belongs to the previous day (an 8pm–midnight event is one day, not two).
 */
export function eventDayKeys(ev: GridEvent): string[] {
  if (ev.isAllDay && ev.startDate) {
    const end = ev.endDate && ev.endDate > ev.startDate ? ev.endDate : addDaysKey(ev.startDate, 1);
    const keys: string[] = [];
    for (let k = ev.startDate; k < end; k = addDaysKey(k, 1)) keys.push(k);
    return keys;
  }
  const start = new Date(ev.startsAt);
  const end = new Date(ev.endsAt);
  if (Number.isNaN(start.getTime())) return [];
  const lastInstant = end.getTime() > start.getTime() ? new Date(end.getTime() - 1) : start;
  const keys: string[] = [];
  for (let d = startOfLocalDay(start); d <= lastInstant; d = addDaysLocal(d, 1)) keys.push(localDateKey(d));
  return keys;
}

/** Index events by the day keys they cover. */
export function bucketByDay<T extends GridEvent>(events: T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const ev of events) {
    for (const key of eventDayKeys(ev)) {
      const list = map.get(key);
      if (list) list.push(ev);
      else map.set(key, [ev]);
    }
  }
  for (const list of map.values()) list.sort(compareEvents);
  return map;
}

/** All-day first, then by start, then longer first. */
export function compareEvents(a: GridEvent, b: GridEvent): number {
  if (a.isAllDay !== b.isAllDay) return a.isAllDay ? -1 : 1;
  const s = Date.parse(a.startsAt) - Date.parse(b.startsAt);
  if (s !== 0) return s;
  return Date.parse(b.endsAt) - Date.parse(a.endsAt);
}

export interface TimedPlacement<T> {
  event: T;
  /** Minutes from local midnight where the block starts (clamped to the day). */
  top: number;
  /** Minutes tall (min 15 so a zero-length event stays visible). */
  height: number;
  /** Horizontal lane within its overlap cluster. */
  lane: number;
  /** Lane count of the cluster this block belongs to. */
  lanes: number;
}

/**
 * Place a day's timed events into side-by-side lanes. Overlapping events share
 * width; independent events take the full column. Greedy first-fit on events
 * sorted by start, which is what every calendar UI does.
 */
export function layoutTimedDay<T extends GridEvent>(dayKey: string, events: T[]): TimedPlacement<T>[] {
  const dayStart = fromDateKey(dayKey).getTime();
  const dayEnd = dayStart + 24 * 60 * 60_000;
  const items = events
    .filter((e) => !e.isAllDay)
    .map((e) => {
      const s = Math.max(Date.parse(e.startsAt), dayStart);
      const eRaw = Math.min(Date.parse(e.endsAt), dayEnd);
      const top = Math.floor((s - dayStart) / 60_000);
      const height = Math.max(15, Math.round((eRaw - s) / 60_000));
      return { event: e, top, height, endMin: top + height };
    })
    .sort((a, b) => a.top - b.top || b.endMin - a.endMin);

  const out: TimedPlacement<T>[] = [];
  let cluster: { laneEnds: number[]; members: TimedPlacement<T>[]; clusterEnd: number } | null = null;

  for (const it of items) {
    if (!cluster || it.top >= cluster.clusterEnd) {
      if (cluster) for (const m of cluster.members) m.lanes = cluster.laneEnds.length;
      cluster = { laneEnds: [], members: [], clusterEnd: 0 };
    }
    let lane = cluster.laneEnds.findIndex((end) => end <= it.top);
    if (lane === -1) {
      lane = cluster.laneEnds.length;
      cluster.laneEnds.push(it.endMin);
    } else {
      cluster.laneEnds[lane] = it.endMin;
    }
    cluster.clusterEnd = Math.max(cluster.clusterEnd, it.endMin);
    const placed: TimedPlacement<T> = { event: it.event, top: it.top, height: it.height, lane, lanes: 1 };
    cluster.members.push(placed);
    out.push(placed);
  }
  if (cluster) for (const m of cluster.members) m.lanes = cluster.laneEnds.length;
  return out;
}

export function formatTimeShort(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes();
  const suffix = h < 12 ? 'a' : 'p';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hh}${suffix}` : `${hh}:${pad2(m)}${suffix}`;
}

export function formatTimeRange(startIso: string, endIso: string): string {
  return `${formatTimeShort(startIso)} – ${formatTimeShort(endIso)}`;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3));

export function monthLabel(year: number, month0: number): string {
  return `${MONTHS[month0]} ${year}`;
}

export function rangeLabel(r: DateRange): string {
  const last = addDaysLocal(r.to, -1);
  if (r.from.getFullYear() !== last.getFullYear()) {
    return `${MONTHS_SHORT[r.from.getMonth()]} ${r.from.getDate()}, ${r.from.getFullYear()} – ${MONTHS_SHORT[last.getMonth()]} ${last.getDate()}, ${last.getFullYear()}`;
  }
  if (r.from.getMonth() !== last.getMonth()) {
    return `${MONTHS_SHORT[r.from.getMonth()]} ${r.from.getDate()} – ${MONTHS_SHORT[last.getMonth()]} ${last.getDate()}, ${last.getFullYear()}`;
  }
  return `${MONTHS[r.from.getMonth()]} ${r.from.getDate()} – ${last.getDate()}, ${last.getFullYear()}`;
}

export function dayHeading(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}
